import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Grid2, MenuItem, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { CheckboxTextField } from "@/controls/CheckboxTextField";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import type { Printer, PrinterForm, PrinterStation } from "../models";
import { useSavePrinter } from "../hooks/usePrinters";

const EMPTY: PrinterForm = {
	PrinterId: null,
	AssetId: "",
	PrinterName: "",
	IpAddress: "",
	Port: 9100,
	IsActive: true,
	Driver: "ZebraZpl",
};

type Props = {
	open: boolean;
	printer: Printer | null;
	stations: PrinterStation[];
	onHide: () => void;
};

export const PrinterFormModal = ({
	open,
	printer,
	stations,
	onHide,
}: Props) => {
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const { control, reset, handleSubmit } = useForm<PrinterForm>({
		defaultValues: EMPTY,
	});
	const save = useSavePrinter(
		() => {
			setNotification(
				`The printer was ${printer ? "updated" : "created"} successfully`,
			);
			onHide();
		},
		(e) => setError(e.message),
	);

	useEffect(() => {
		if (open) reset(printer ? { ...printer } : EMPTY);
	}, [open, printer, reset]);

	return (
		<>
			{open && (
				<HelmetDexteel
					title={`${printer ? "Edit" : "New"} Printer - Printers`}
				/>
			)}
			<MesfModal
				id="modal-printer"
				title={printer ? "EDIT PRINTER" : "CREATE PRINTER"}
				open={open}
				handleClose={() => !save.isLoading && onHide()}
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit((v) => save.mutate(v))} noValidate>
					<MesfModal.Content style={{ padding: "15px 30px" }}>
						<Grid2 container spacing={2}>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="PrinterName"
									control={control}
									rules={{ required: "Name is required" }}
									render={({ field, fieldState: { error } }) => (
										<TextField
											{...field}
											label="Name"
											size="small"
											fullWidth
											autoFocus
											autoComplete="off"
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="AssetId"
									control={control}
									rules={{ required: "Station is required" }}
									render={({ field, fieldState: { error } }) => (
										<TextField
											{...field}
											select
											label="Station"
											size="small"
											fullWidth
											error={!!error}
											helperText={error?.message}
										>
											{[...stations]
												.sort((a, b) => a.AssetName.localeCompare(b.AssetName))
												.map((s) => {
													const taken =
														s.PrinterId != null &&
														s.PrinterId !== printer?.PrinterId;
													return (
														<MenuItem
															key={s.AssetId}
															value={s.AssetId}
															disabled={taken}
														>
															{s.AssetName}
															{taken ? " (has a printer)" : ""}
														</MenuItem>
													);
												})}
										</TextField>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 8 }}>
								<Controller
									name="IpAddress"
									control={control}
									rules={{ required: "IP address is required" }}
									render={({ field, fieldState: { error } }) => (
										<TextField
											{...field}
											label="IP address"
											size="small"
											fullWidth
											autoComplete="off"
											placeholder="192.168.1.10"
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 4 }}>
								<Controller
									name="Port"
									control={control}
									rules={{ required: "Port is required" }}
									render={({ field, fieldState: { error } }) => (
										<TextField
											{...field}
											label="Port"
											type="number"
											size="small"
											fullWidth
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: printer ? 6 : 12 }}>
								<Controller
									name="Driver"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											select
											label="Driver"
											size="small"
											fullWidth
										>
											<MenuItem value="ZebraZpl">Zebra ZPL</MenuItem>
										</TextField>
									)}
								/>
							</Grid2>
							{printer && (
								<Grid2 size={{ xs: 6 }}>
									<TextField
										label="MAC address"
										size="small"
										fullWidth
										disabled
										value={printer.MacAddress ?? "—"}
									/>
								</Grid2>
							)}
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<CheckboxTextField
											label="Is Active"
											checked={field.value}
											onChange={field.onChange}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={save.isLoading}
							onHide={onHide}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
